/**
 * Cliente HTTP de auth compartido de la familia de apps (extraído de speak).
 *
 * Thin `fetch` wrappers sobre las rutas de auth del backend (`/api/auth/*`).
 * En dev `/api` se sirve same-origin vía el proxy de Vite de cada app, así la
 * cookie httpOnly (`orborix.session-token`) se setea y reenvía sola;
 * `credentials: 'include'` es defensivo (funciona también cross-origin).
 *
 * Flujo passwordless: OTP por email (`/otp/request` + `/otp/verify`), que
 * registra y loguea con el mismo código. Las rutas con password siguen
 * existiendo en el backend pero las apps de la familia no las consumen.
 *
 * Backend contract (read-only — `apps/backend/src/routes/auth/`):
 *   POST /api/auth/otp/request {email}       -> 200 {ok:true}  (siempre, anti-enum)
 *   POST /api/auth/otp/verify  {email,code,nativeLanguage} -> 200 {user,isNewUser} (400,429)
 *   GET  /api/auth/me                        -> 200 {user} | 401  (user.otaEarlyAccess, user.aiDataConsentRequired)
 *   POST /api/auth/me/ai-data-consent {version} -> 200 {user} (400, 409 stale_consent_version)
 *   POST /api/auth/signout                   -> 200 {ok:true}
 *   PATCH /api/auth/me {name,locale,timezone}-> 200 {user}  (full-replace)
 *   DELETE /api/auth/me {otpCode}            -> 200 {ok:true} (403,429)
 *   POST /api/auth/native/google  {idToken,nonce,nativeLanguage}            -> 200 {user,isNewUser,token}
 *   POST /api/auth/native/apple   {identityToken,nonce,authorizationCode?,…} -> 200 {user,isNewUser,token}
 *   POST /api/auth/native/apple/android/start    {challenge,nativeLanguage} -> 200 {authorizeUrl,returnUrl}
 *   POST /api/auth/native/apple/android/complete {handoff,verifier}         -> 200 {user,isNewUser,token}
 *   (native social login: docs/speak-plan/50-native-social-login.md § 3.4)
 *
 * Errors are mapped to Spanish, user-facing messages via `AuthError`.
 */

import { apiFetch } from './apiClient.js';
import { getAuthClientConfig } from './config.js';

export interface AuthUser {
  id: string;
  tenantId: string;
  email: string;
  emailVerified: boolean;
  name: string | null;
  locale: string;
  timezone: string;
  /**
   * OTA early access (speak plan 49 § 3.3): `true` when this account is in
   * `module_config.languages.otaEarlyAccessEmails`, so the native app marks
   * its updater checks with `ota-early` and gets a published bundle before
   * the public rollout. The backend sends it — the caller's own flag only —
   * on every auth response that carries the caller's user (`GET`/`PATCH
   * /api/auth/me`, `/otp/verify`, `/signin`, `/signup`). Optional only so
   * fixtures and the other apps need not set it; absent reads as `false`.
   */
  otaEarlyAccess?: boolean;
  /**
   * AI data consent (speak store review): `true` until the account accepts the
   * CURRENT consent text (`POST /api/auth/me/ai-data-consent`). Sent on every
   * auth response that carries the caller's user. Optional only so fixtures
   * and the other apps need not set it; speak treats absent as `true` (fail
   * closed — it shows its consent screen).
   */
  aiDataConsentRequired?: boolean;
  /** When the current consent text was accepted (ISO), `null` when it was not. */
  aiDataConsentAt?: string | null;
  /**
   * The «Estadísticas de uso» switch of speak's Perfil (`users.analytics_opt_out`):
   * `true` = speak sends no product analytics while signed in to this account.
   * Sent on the same responses as `aiDataConsentRequired`; set through
   * PATCH /api/me/preferences. Optional only so fixtures and the other apps need
   * not set it; absent reads as `false` (first-party analytics on by default).
   */
  analyticsOptOut?: boolean;
  /**
   * Providers linked to the account (Sign in with Apple / Google), sorted; `[]`
   * for an email-only account. Sent with `otaEarlyAccess` on the same
   * responses. With one linked, speak's onboarding does not ask for the name:
   * after Sign in with Apple, asking for data Apple provides is an App Store
   * rejection (Guideline 4; speak plan 50 § 9). Optional only so fixtures and
   * the other apps need not set it; absent reads as `[]`.
   */
  oauthProviders?: ('apple' | 'google')[];
}

/** Error carrying a user-facing Spanish message + the raw backend code. */
export class AuthError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
    this.status = status;
  }
}

interface UserResponse {
  user: AuthUser;
}

interface ErrorResponse {
  error?: string;
}

const ERROR_MESSAGES: Record<string, string> = {
  native_language_required: 'Falta tu idioma nativo — pedí un código nuevo e intentá otra vez',
  rate_limited: 'Demasiados intentos, probá en un minuto',
  invalid_input: 'Revisá el email: no parece válido',
  invalid_code: 'El código no es válido o caducó',
  too_many_attempts: 'Demasiados intentos. Pedí un código nuevo.',
};

const GENERIC_MESSAGE = 'Algo salió mal. Probá de nuevo en un momento.';

function isUserResponse(value: unknown): value is UserResponse {
  if (typeof value !== 'object' || value === null) return false;
  const user = (value as { user?: unknown }).user;
  return typeof user === 'object' && user !== null && typeof (user as AuthUser).id === 'string';
}

async function parseJson(res: Response): Promise<unknown> {
  return res.json().catch(() => null);
}

/** Map a non-OK response to a user-facing `AuthError`. */
async function toAuthError(res: Response): Promise<AuthError> {
  const body = (await parseJson(res)) as ErrorResponse | null;
  const code = typeof body?.error === 'string' ? body.error : 'unknown';
  const message = ERROR_MESSAGES[code] ?? GENERIC_MESSAGE;
  return new AuthError(message, code, res.status);
}

async function authFetch(path: string, init?: RequestInit): Promise<Response> {
  try {
    return await apiFetch(path, {
      headers: {
        'Content-Type': 'application/json',
        // Identidad de la app para el audit de auth (auth_events.app_source).
        'X-Orborix-App': getAuthClientConfig().appSource,
      },
      ...init,
    });
  } catch {
    // Network failure (server down, offline, CORS, etc.).
    throw new AuthError(
      'No pudimos conectar con el servidor. Revisá tu conexión.',
      'network_error',
      0,
    );
  }
}

async function readUser(res: Response): Promise<AuthUser> {
  if (!res.ok) throw await toAuthError(res);
  const body = await parseJson(res);
  if (!isUserResponse(body)) {
    throw new AuthError(GENERIC_MESSAGE, 'malformed_response', res.status);
  }
  return body.user;
}

/** Returns the current user, or `null` when unauthenticated (401). */
export async function me(): Promise<AuthUser | null> {
  const res = await authFetch('/api/auth/me', { method: 'GET' });
  if (res.status === 401) return null;
  return readUser(res);
}

export async function signout(): Promise<void> {
  try {
    await authFetch('/api/auth/signout', { method: 'POST' });
  } finally {
    // Nativo: borramos el token aunque el signout de red falle, para que la
    // sesión local no sobreviva. No-op en web (la cookie la revoca el backend).
    await getAuthClientConfig().native?.clearToken();
  }
}

/**
 * Pide un código OTP de 6 dígitos por email. Anti-enumeración: el backend
 * responde `200 {ok:true}` siempre (exista o no la cuenta); sólo propagamos
 * errores de red.
 */
export async function requestOtp(email: string): Promise<void> {
  await authFetch('/api/auth/otp/request', { method: 'POST', body: JSON.stringify({ email }) });
}

export interface OtpVerifyResult {
  user: AuthUser;
  /** `true` cuando el código creó la cuenta (signup) — el caller rutea al onboarding. */
  isNewUser: boolean;
}

/**
 * Verifica el código OTP. Crea la cuenta si el email es nuevo (passwordless),
 * con la lengua nativa que la app declaró (`signupNativeLanguage`).
 */
export async function verifyOtp(email: string, code: string): Promise<OtpVerifyResult> {
  const nativeLanguage = getAuthClientConfig().signupNativeLanguage;
  const res = await authFetch('/api/auth/otp/verify', {
    method: 'POST',
    body: JSON.stringify({ email, code, nativeLanguage }),
  });
  return readSession(res);
}

/**
 * Reads a session answer (`200 { user, isNewUser, token? }`) — the shape of
 * `otp/verify` and of every native social-login endpoint.
 */
async function readSession(res: Response): Promise<OtpVerifyResult> {
  if (!res.ok) throw await toAuthError(res);
  const body = await parseJson(res);
  if (!isUserResponse(body)) {
    throw new AuthError(GENERIC_MESSAGE, 'malformed_response', res.status);
  }
  const isNewUser = (body as { isNewUser?: unknown }).isNewUser === true;
  // Login nativo: cuando llega `X-Orborix-Native: 1` (lo agrega `apiFetch`),
  // el backend incluye el JWT de sesión en el body. Lo persistimos vía el
  // bridge para que la sesión nativa sobreviva al reinicio de la app. No-op en
  // web: el body no trae `token` y la sesión vive en la cookie.
  const token = (body as { token?: unknown }).token;
  if (typeof token === 'string') await getAuthClientConfig().native?.persistToken(token);
  return { user: body.user, isNewUser };
}

/**
 * URL that starts Google OAuth (full top-level navigation, not fetch). Carries
 * the native language the app signs users up with: the callback may CREATE the
 * account, and the backend rejects `/api/auth/google` without it.
 */
export function googleSignInUrl(): string {
  const nativeLanguage = getAuthClientConfig().signupNativeLanguage;
  return `/api/auth/google?nativeLanguage=${encodeURIComponent(nativeLanguage)}`;
}

/**
 * URL that starts Sign in with Apple on the web (full top-level navigation, like
 * `googleSignInUrl`). The callback answers 303 to `/home`, `/welcome` or
 * `/login?error=oauth_<code>` (speak plan 50 § 3.4).
 */
export function appleSignInUrl(): string {
  const nativeLanguage = getAuthClientConfig().signupNativeLanguage;
  return `/api/auth/apple?nativeLanguage=${encodeURIComponent(nativeLanguage)}`;
}

// ─── Native social login (Capacitor apps; speak plan 50 § 3.4) ──────
//
// Every call answers like `otp/verify` (`{ user, isNewUser, token }`, the token
// persisted through the native bridge) or throws an `AuthError` whose `code` is
// the backend's: invalid_input, native_language_required, invalid_token,
// email_unverified, apple_no_email, flow_expired, rate_limited, oauth_network,
// oauth_misconfigured. The message is the generic one: the copy depends on the
// provider and the platform, so the app maps the code itself. `nativeLanguage`
// travels on every call that may CREATE the account, as in `verifyOtp`.

/**
 * Google ID token from the OS (GIDSignIn on iOS, Credential Manager on Android).
 * `nonce` is the RAW value: Google got `sha256hex(nonce)` and put it in the token.
 */
export async function signInWithGoogleIdToken(input: {
  idToken: string;
  nonce: string;
}): Promise<OtpVerifyResult> {
  const nativeLanguage = getAuthClientConfig().signupNativeLanguage;
  const res = await authFetch('/api/auth/native/google', {
    method: 'POST',
    body: JSON.stringify({ ...input, nativeLanguage }),
  });
  return readSession(res);
}

/**
 * Apple identity token from the iOS sheet (ASAuthorization). Same raw-nonce rule
 * as Google. Apple shares the name only on the first authorization ever.
 */
export async function signInWithAppleIdentityToken(input: {
  identityToken: string;
  nonce: string;
  authorizationCode?: string;
  givenName?: string;
  familyName?: string;
}): Promise<OtpVerifyResult> {
  const nativeLanguage = getAuthClientConfig().signupNativeLanguage;
  const res = await authFetch('/api/auth/native/apple', {
    method: 'POST',
    body: JSON.stringify({ ...input, nativeLanguage }),
  });
  return readSession(res);
}

/** What `POST /native/apple/android/start` answers. */
export interface AppleAndroidStart {
  /** Apple's authorize URL, opened in a Chrome Custom Tab. */
  authorizeUrl: string;
  /** `${FRONTEND_URL}/auth/apple/android-return`: where the server bounces back to. */
  returnUrl: string;
}

/**
 * Android Apple flow, step 1: registers the PKCE `challenge`
 * (base64url(sha256(verifier))) and gets the URL to open. The native language
 * travels here: `/complete` creates the account with it.
 */
export async function startAppleAndroidSignIn(challenge: string): Promise<AppleAndroidStart> {
  const nativeLanguage = getAuthClientConfig().signupNativeLanguage;
  const res = await authFetch('/api/auth/native/apple/android/start', {
    method: 'POST',
    body: JSON.stringify({ challenge, nativeLanguage }),
  });
  if (!res.ok) throw await toAuthError(res);
  const body = (await parseJson(res)) as Partial<AppleAndroidStart> | null;
  if (typeof body?.authorizeUrl !== 'string' || typeof body.returnUrl !== 'string') {
    throw new AuthError(GENERIC_MESSAGE, 'malformed_response', res.status);
  }
  return { authorizeUrl: body.authorizeUrl, returnUrl: body.returnUrl };
}

/** Android Apple flow, last step: the handoff from the return link + the PKCE verifier. */
export async function completeAppleAndroidSignIn(input: {
  handoff: string;
  verifier: string;
}): Promise<OtpVerifyResult> {
  const res = await authFetch('/api/auth/native/apple/android/complete', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return readSession(res);
}

/**
 * Borra la cuenta del user logueado (y todos sus datos). La baja se confirma
 * con un código OTP de 6 dígitos pedido antes vía `requestOtp` — el único
 * factor que las cuentas passwordless poseen. Tras el 200 la sesión del server
 * ya no existe; acá limpiamos el token nativo (no-op en web) — el caller debe
 * además llamar a `signout()` del AuthProvider para vaciar el estado local.
 */
export async function deleteAccount(otpCode: string): Promise<void> {
  const res = await authFetch('/api/auth/me', {
    method: 'DELETE',
    body: JSON.stringify({ otpCode }),
  });
  if (!res.ok) throw await toAuthError(res);
  await getAuthClientConfig().native?.clearToken();
}

/**
 * Actualiza el perfil del user. El PATCH del backend es full-replace (exige
 * name + locale + timezone) — el caller manda los valores actuales para los
 * campos que no cambia.
 */
export async function updateProfile(input: {
  name: string | null;
  locale: string;
  timezone: string;
}): Promise<AuthUser> {
  const res = await authFetch('/api/auth/me', {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  return readUser(res);
}
